import { Theme, css } from '@emotion/react';

export const partyCardCssDefs = {
  partyCard:
    ({
      isSelected,
      isDragging,
      isCompactView,
    }: {
      isSelected: boolean;
      isDragging: boolean;
      isCompactView: boolean;
    }) =>
    ({ spacing, colors }: Theme) => css`
      display: flex;
      flex-direction: row;
      gap: ${spacing(1)}px;
      padding: ${!isCompactView && `${spacing(0.5)}px`};
      user-select: none;
      ${isCompactView && `height: 40px; padding-left: ${spacing(0.5)}px;`}

      & button:last-child {
        // -8px in compact view compensates for the width of the
        // Otherwise visit notes just before a status indicator button get too much effective
        // visual margin-right space
        margin-left: ${isCompactView ? spacing(-0.5) + 'px' : 'auto'};
      }

      ${isDragging &&
      `
        cursor: move;
        opacity: 0.5;
      `}

      &:hover {
        background-color: ${colors.bgLighter};
        cursor: pointer;

        & button {
          background-color: ${colors.bgLight};

          :hover {
            background-color: ${colors.bgBase};
          }
        }

        [data-party-size-indicator='true'] {
          background-color: ${colors.bgLight};
        }
      }

      ${isSelected &&
      `
        background-color: ${colors.bgLighter};

        & button {
          background-color: ${colors.bgLight};
        }

        [data-party-size-indicator='true'] {
          background-color: ${colors.bgLight};
        }
      `}
    `,
  container:
    ({ isCompactView, hideStatusBar }: { isCompactView: boolean; hideStatusBar?: boolean }) =>
    ({ spacing }: Theme) => [
      css`
        display: ${isCompactView ? 'grid' : 'flex'};
        flex: 1;
      `,
      isCompactView &&
        css`
          grid-template-columns: 225px auto;
          gap: 12px;
          align-items: stretch;
        `,
      !isCompactView &&
        css`
          flex-direction: column;
          padding: ${spacing(0.5)}px;
        `,
      !isCompactView &&
        !hideStatusBar &&
        css`
          padding-left: 0;
        `,
    ],
  // all necessary padding/margin here is in the party cards themselves, we just need to draw the rules
  horizontalRule: ({ colors }: Theme) => css`
    background-color: ${colors.bgLight};
    height: 1px;
    width: 100%;
  `,
};

export const partyCardStatusIndicatorCssDefs = {
  root:
    (
      props: {
        showUnassignedWarning?: boolean;
        isCompactView?: boolean;
        noGrabCursor?: boolean;
      } = {}
    ) =>
    ({ colors, borderRadius }: Theme) => css`
      background: ${colors.bgLighter};
      color: ${colors.fgDefault};
      align-items: center;
      align-self: stretch;
      border-radius: ${props.isCompactView ? '0px' : `${borderRadius[100]}`};
      ${!props.noGrabCursor &&
      `
      cursor: grab;
      `}
      display: flex;
      flex: 0 0 6rem;
      flex-direction: column;
      justify-content: center;

      &:hover {
        background: ${colors.bgBase};
      }

      ${props.showUnassignedWarning &&
      `
      background-color: ${colors.bgDangerLighter};
      color: ${colors.fgDangerBase};
      `}
    `,
  arrivalTimer: ({ colors, borderRadius, spacing }: Theme) => css`
    background-color: ${colors.bgLight};
    border-radius: ${borderRadius[100]};
    color: ${colors.fgDefault};
    display: flex;
    padding: 0 ${spacing(0.5)}px 0 ${spacing(0.5)}px;
    height: 20px;
    align-items: center;
  `,
};

export const partyCardStatusBarCssDefs = {
  statusBar:
    ({ isCompactView, barColor }: { isCompactView: boolean; barColor: string }) =>
    ({ spacing }: Theme) => css`
      background-color: ${barColor};
      border-radius: 2px;
      width: ${isCompactView ? `${spacing(1.5)}px` : `${spacing(1)}px`};
      ${isCompactView && `margin: 4px 0`};
    `,
};

export const partyCardHeaderCssDefs = {
  header: ({ hasTime }: { hasTime: boolean }) => css`
    display: flex;
    justify-content: space-between;
    ${hasTime && `height: 16px;`}
  `,
  partySizeIndicator:
    ({ isLargeParty, isCompactView }: { isLargeParty: boolean; isCompactView: boolean }) =>
    ({ colors, spacing, borderRadius }: Theme) => css`
      align-items: center;
      background-color: ${isLargeParty ? colors.bgWarningLighter : colors.bgLighter};
      border-radius: ${borderRadius[100]};
      display: flex;
      max-width: fit-content;
      gap: ${spacing(0.5)}px;
      height: 20px;
      padding: 0 ${spacing(0.5)}px 0 ${spacing(0.5)}px;
      ${isLargeParty && `color: ${colors.bgWarningDark}`};
      ${isCompactView && `margin-left: auto`};
    `,
  icon: css`
    height: 16px;
    width: 16px;
  `,
};

export const partyCardDescriptionCssDefs = {
  descriptionContainer: css`
    display: flex;
    flex-direction: column;
    min-width: 0;
    justify-content: center;
  `,
  descriptionContainerCompact: ({ colors }: Theme) => css`
    border-right: 1px solid ${colors.borderDefault};
    text-align: center;
    width: 145px;
    min-width: 145px;
    padding-right: 12px;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
  `,
  descriptionContent: ({ isCompactView }: { isCompactView: boolean }) => css`
    align-items: center;
    display: flex;
    flex-wrap: ${isCompactView ? 'nowrap' : 'wrap'};
    gap: ${isCompactView ? '12px' : '4px'};
    ${isCompactView && `height: 100%`};
  `,
  iconContainer: css`
    display: flex;
    gap: 4px;
    align-items: center;
  `,
  notesAndDurationContainer: css`
    margin-left: auto;
    display: flex;
    gap: 12px;
    min-width: 0;
  `,
  icon: css`
    height: 16px;
    width: 16px;
    vertical-align: middle;
  `,
  textOverflow: css`
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    max-width: 100%;
  `,
  visitNotes: css`
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow-wrap: anywhere;
    text-overflow: ellipsis;
  `,
  compactVisitNotesOuter: ({ spacing }: Theme) => css`
    overflow: hidden;
    margin-left: auto;
    margin-right: ${spacing(1.5)}px;
    text-align: right;
    width: 100%;
  `,
  compactVisitNotesInner: css`
    display: block;
    overflow: hidden;
    overflow-wrap: anywhere;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
};

export const partyCardCompactDescriptionCssDefs = {
  descriptionContainer: css`
    display: flex;
    flex-direction: row;
    height: 40px;
  `,
  //padding-right below is a work around to get the contents in reservationDescription to center properly.
  //the parent component is actually bumping the container over by 12px. When compact view is refactored remove this padding
  reservationDescription: ({ colors }: Theme) => css`
    align-items: center;
    border-right: 1px solid;
    border-color: ${colors.borderDefault};
    display: flex;
    padding-right: 12px;
    justify-content: center;
    width: 145px;
    min-width: 145px;
  `,
  visitCountContainer: ({ colors, spacing }: Theme) => css`
    align-items: center;
    border-right: 1px solid;
    border-color: ${colors.borderDefault};
    display: flex;
    gap: ${spacing(0.25)}px;
    justify-content: center;
    width: 50px;
    min-width: 50px;
  `,
  visitCountIcon:
    ({ noGuestProfile }: { noGuestProfile: boolean }) =>
    ({ colors }: Theme) => css`
      color: ${noGuestProfile ? colors.fgSubtle : colors.fgFeatureGateBase};
      height: 18px;
      width: 18px;
    `,
  visitCount:
    ({ noGuestProfile }: { noGuestProfile: boolean }) =>
    ({ colors }: Theme) => css`
      color: ${noGuestProfile ? colors.fgSubtle : colors.fgFeatureGateBase};
    `,
  iconsContainer: ({ spacing }: Theme) => css`
    align-items: center;
    justify-content: space-between;
    display: flex;
    gap: ${spacing(1)}px;
    padding: 0 ${spacing(1.5)}px;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
  `,
};
